import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

const getAuthHeaders = () => {
    const token = getAccessToken();

    if (!token) {
        window.location.href = "/";
    }

    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
};

const buildPeriodParams = ({ periodFrom, periodTo }) => ({
    period_from: periodFrom,
    period_to: periodTo,
});

const buildScopedPeriodParams = ({ periodFrom, periodTo, businessEntityId, businessAccountId }) => {
    const params = buildPeriodParams({ periodFrom, periodTo });
    if (businessEntityId) {
        params.business_entity_id = businessEntityId;
    }
    if (businessAccountId) {
        params.business_account_id = businessAccountId;
    }
    return params;
};

export const getPaymentManagementSummary = async ({ periodFrom, periodTo, businessEntityId, businessAccountId }) => {
    const response = await axios.get(`${API_BASE_URL}/payment-reports/management-summary`, {
        ...getAuthHeaders(),
        params: buildScopedPeriodParams({ periodFrom, periodTo, businessEntityId, businessAccountId }),
    });
    return response.data;
};

export const getPaymentsReport = async ({ periodFrom, periodTo, limit = 500, qualityIssue, businessEntityId, businessAccountId }) => {
    const params = { ...buildPeriodParams({ periodFrom, periodTo }), limit };
    if (qualityIssue) {
        params.quality_issue = qualityIssue;
    }
    if (businessEntityId) {
        params.business_entity_id = businessEntityId;
    }
    if (businessAccountId) {
        params.business_account_id = businessAccountId;
    }
    const response = await axios.get(`${API_BASE_URL}/payment-reports/payments`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const downloadPaymentsExport = async ({ periodFrom, periodTo, businessEntityId, businessAccountId }) => {
    const params = buildPeriodParams({ periodFrom, periodTo });
    if (businessEntityId) {
        params.business_entity_id = businessEntityId;
    }
    if (businessAccountId) {
        params.business_account_id = businessAccountId;
    }
    const response = await axios.get(`${API_BASE_URL}/payment-reports/export.xlsx`, {
        ...getAuthHeaders(),
        params,
        responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }));
    const link = document.createElement("a");
    link.href = url;
    const filenameParts = ["payments", periodFrom, periodTo];
    if (businessEntityId) {
        filenameParts.push(`entity-${businessEntityId}`);
    }
    if (businessAccountId) {
        filenameParts.push(`account-${businessAccountId}`);
    }
    link.download = `${filenameParts.join("_")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const getPaymentUnverifiedEntities = async () => {
    const response = await axios.get(`${API_BASE_URL}/payment-reports/unverified-entities`, {
        ...getAuthHeaders(),
    });
    return response.data;
};

export const getPaymentSummary = async ({ periodFrom, periodTo, businessEntityId, businessAccountId }) => {
    const response = await axios.get(`${API_BASE_URL}/payment-reports/summary`, {
        ...getAuthHeaders(),
        params: buildScopedPeriodParams({ periodFrom, periodTo, businessEntityId, businessAccountId }),
    });
    return response.data;
};

export const importSalesDrivePayments = async ({ periodFrom, periodTo, paymentType = "all" }) => {
    const response = await axios.post(
        `${API_BASE_URL}/payment-imports/salesdrive`,
        {
            period_from: periodFrom,
            period_to: periodTo,
            payment_type: paymentType,
        },
        getAuthHeaders(),
    );
    return response.data;
};

export const recalculatePaymentReport = async ({ periodFrom, periodTo }) => {
    const response = await axios.post(
        `${API_BASE_URL}/payment-reports/recalculate`,
        {
            period_from: periodFrom,
            period_to: periodTo,
        },
        getAuthHeaders(),
    );
    return response.data;
};

export const getPaymentImportRuns = async ({ limit = 10 } = {}) => {
    const response = await axios.get(`${API_BASE_URL}/payment-imports`, {
        ...getAuthHeaders(),
        params: { limit },
    });
    return response.data;
};

export const upsertAccountBalanceAdjustment = async (payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/payment-reports/account-balance-adjustments`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const getPaymentUnmappedCounterparties = async ({ periodFrom, periodTo, limit = 100, examples = 2 }) => {
    const response = await axios.get(`${API_BASE_URL}/payment-reports/unmapped-counterparties`, {
        ...getAuthHeaders(),
        params: { ...buildPeriodParams({ periodFrom, periodTo }), limit, examples },
    });
    return response.data;
};

export const getPaymentCounterpartyMappings = async ({ limit = 500 } = {}) => {
    const response = await axios.get(`${API_BASE_URL}/payment-reports/counterparty-mappings`, {
        ...getAuthHeaders(),
        params: { limit },
    });
    return response.data;
};

export const createPaymentCounterpartyMapping = async (payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/payment-reports/counterparty-mappings`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const getPaymentIncomingClassificationRules = async ({ limit = 500 } = {}) => {
    const response = await axios.get(`${API_BASE_URL}/payment-reports/incoming-classification-rules`, {
        ...getAuthHeaders(),
        params: { limit },
    });
    return response.data;
};

export const createPaymentIncomingClassificationRule = async (payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/payment-reports/incoming-classification-rules`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const updatePaymentIncomingClassificationRule = async (ruleId, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/payment-reports/incoming-classification-rules/${ruleId}`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const getPaymentOutgoingClassificationRules = async ({ limit = 500 } = {}) => {
    const response = await axios.get(`${API_BASE_URL}/payment-reports/outgoing-classification-rules`, {
        ...getAuthHeaders(),
        params: { limit },
    });
    return response.data;
};

export const createPaymentOutgoingClassificationRule = async (payload) => {
    const response = await axios.post(
        `${API_BASE_URL}/payment-reports/outgoing-classification-rules`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const updatePaymentOutgoingClassificationRule = async (ruleId, payload) => {
    const response = await axios.patch(
        `${API_BASE_URL}/payment-reports/outgoing-classification-rules/${ruleId}`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};
